// Disposable Tenant Admin API test UI for Data Connections.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiHttpError,
  type ApiConfig,
  DEFAULT_BASE_URL,
  getDataHealthPipeline,
  listLogs,
  RETIRED_BASE_URL,
} from "./api";
import { formatTenant } from "./format";
import {
  type ProfileStore,
  addProfile,
  deleteProfile,
  getActive,
  loadStore,
  saveStore,
  selectProfile,
  updateProfile,
} from "./profiles";
import { AlarmsPanel } from "./components/AlarmsPanel";
import { InternalLineagePanel } from "./components/InternalLineagePanel";
import { ConfigPanel } from "./components/ConfigPanel";
import { DrillDown } from "./components/DrillDown";
import { FilterBar } from "./components/FilterBar";
import { IntegrationsPanel } from "./components/IntegrationsPanel";
import { LogRow } from "./components/LogRow";
import { OverviewPanel } from "./components/OverviewPanel";
import { PipelinePanel } from "./components/PipelinePanel";
import { WinbackPanel } from "./components/WinbackPanel";
import type { LevelFilter, PipelineCadence, PipelineEvent } from "./types";

type View =
  | "overview"
  | "integrations"
  | "pipeline"
  | "modelgraph"
  | "logs"
  | "winback"
  | "alarms";

const POLL_INTERVAL_MS = 3000;
const PAGE_SIZE = 100;

function fixBaseUrl(url: string): string {
  return RETIRED_BASE_URL.test(url) ? DEFAULT_BASE_URL : url;
}

export function App() {
  const [store, setStore] = useState<ProfileStore>(() => loadStore(localStorage, fixBaseUrl));
  const [panel, setPanel] = useState<"add" | "edit" | null>(null);
  const activeProfile = getActive(store);
  const config: ApiConfig | null = activeProfile;

  const commitStore = (next: ProfileStore): void => {
    saveStore(localStorage, next);
    setStore(next);
  };
  const [view, setView] = useState<View>("overview");
  const [events, setEvents] = useState<PipelineEvent[]>([]);
  const [level, setLevel] = useState<LevelFilter>("all");
  const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set());
  const [live, setLive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drillDown, setDrillDown] = useState<PipelineEvent | null>(null);
  const [cadenceBySource, setCadenceBySource] = useState<Record<string, PipelineCadence>>({});
  const lastTsRef = useRef<string | null>(null);
  // Id of the profile whose responses may still land; a response that resolves
  // after a profile switch belongs to the old region and is dropped.
  const activeIdRef = useRef<string | undefined>(activeProfile?.id);
  activeIdRef.current = activeProfile?.id;

  // Logs state lives above the per-profile subtree, so clear it on switch;
  // otherwise a failed fetch for the new profile keeps the old region's rows.
  useEffect(() => {
    setEvents([]);
    setError(null);
    setCadenceBySource({});
    lastTsRef.current = null;
  }, [activeProfile?.id]);

  const fetchAll = useCallback(async (): Promise<void> => {
    if (!config || view !== "logs") return;
    const profileId = activeIdRef.current;
    try {
      const resp = await listLogs(config, { level, limit: PAGE_SIZE });
      if (activeIdRef.current !== profileId) return;
      setEvents(resp.data);
      setError(null);
      lastTsRef.current = resp.data[0]?.ts_utc ?? null;
    } catch (e) {
      if (activeIdRef.current === profileId) setError(formatError(e));
    }
  }, [config, level, view]);

  const fetchSince = useCallback(async (): Promise<void> => {
    if (!config || view !== "logs" || !lastTsRef.current) return;
    const profileId = activeIdRef.current;
    try {
      const resp = await listLogs(config, {
        level,
        since: lastTsRef.current,
        limit: PAGE_SIZE,
      });
      if (activeIdRef.current !== profileId) return;
      if (resp.data.length > 0) {
        setEvents((prev) => {
          const seen = new Set(prev.map((e) => e.event_id));
          const fresh = resp.data.filter((e) => !seen.has(e.event_id));
          return [...fresh, ...prev].slice(0, 500);
        });
        lastTsRef.current = resp.data[0].ts_utc;
      }
      setError(null);
    } catch (e) {
      if (activeIdRef.current === profileId) setError(formatError(e));
    }
  }, [config, level, view]);

  useEffect(() => {
    if (config) fetchAll();
  }, [config, fetchAll, level]);

  useEffect(() => {
    if (!config || !live || view !== "logs") return;
    const id = setInterval(() => {
      void fetchSince();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [config, fetchSince, live, view]);

  // Fetch each source's live delivery cadence once when the Logs tab opens so
  // the per-row cadence chip reflects the actual schedule. Best-effort: on
  // failure the chip falls back to the pipeline-slug heuristic.
  useEffect(() => {
    if (!config || view !== "logs") return;
    let cancelled = false;
    getDataHealthPipeline(config)
      .then((resp) => {
        if (!cancelled) setCadenceBySource(resp.meta.cadence_by_source ?? {});
      })
      .catch(() => {
        /* best-effort - LogRow falls back to the slug heuristic */
      });
    return () => {
      cancelled = true;
    };
  }, [config, view]);

  const visible = useMemo(() => {
    if (selectedSources.size === 0) return events;
    return events.filter((e) => selectedSources.has(e.source_system));
  }, [events, selectedSources]);

  const sources = useMemo(() => {
    const s = new Set(events.map((e) => e.source_system));
    return Array.from(s).sort();
  }, [events]);

  if (!activeProfile || panel === "add") {
    return (
      <ConfigPanel
        initial={null}
        onSave={(input) => {
          commitStore(addProfile(store, input));
          setPanel(null);
        }}
        onCancel={activeProfile ? () => setPanel(null) : undefined}
      />
    );
  }

  if (panel === "edit") {
    return (
      <ConfigPanel
        initial={activeProfile}
        onSave={(input) => {
          commitStore(updateProfile(store, activeProfile.id, input));
          setPanel(null);
        }}
        onCancel={() => setPanel(null)}
      />
    );
  }

  return (
    <div className="app" key={activeProfile.id}>
      <header className="topbar">
        <div className="brand">
          Growdhi <strong>Data Connections</strong>{" "}
          <span className="tenant-chip">tenant: {formatTenant(activeProfile.tenantId)}</span>{" "}
          <span className="tenant-chip">{activeProfile.baseUrl.replace(/^https?:\/\//, "")}</span>
        </div>
        <nav className="topnav" aria-label="Data Connections views">
          <button
            className={view === "overview" ? "active" : ""}
            onClick={() => setView("overview")}
          >
            Overview
          </button>
          <button
            className={view === "integrations" ? "active" : ""}
            onClick={() => setView("integrations")}
          >
            Integrations
          </button>
          <button
            className={view === "pipeline" ? "active" : ""}
            onClick={() => setView("pipeline")}
          >
            Data Health
          </button>
          <button
            className={view === "winback" ? "active" : ""}
            onClick={() => setView("winback")}
          >
            Winback
          </button>
          <button
            className={view === "modelgraph" ? "active" : ""}
            onClick={() => setView("modelgraph")}
          >
            Model graph
          </button>
          <button
            className={view === "alarms" ? "active" : ""}
            onClick={() => setView("alarms")}
          >
            Alarms
          </button>
          <button className={view === "logs" ? "active" : ""} onClick={() => setView("logs")}>
            Logs
          </button>
        </nav>
        <div className="profile-switcher">
          <select
            value={activeProfile.id}
            onChange={(e) => commitStore(selectProfile(store, e.target.value))}
            aria-label="Connection profile"
          >
            {store.profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <button className="settings-btn" onClick={() => setPanel("add")} title="Add a connection profile">
            Add profile
          </button>
          <button className="settings-btn" onClick={() => setPanel("edit")} title="Edit this profile">
            Edit
          </button>
          <button
            className="settings-btn"
            onClick={() => {
              if (window.confirm(`Delete profile "${activeProfile.name}"?`)) {
                commitStore(deleteProfile(store, activeProfile.id));
              }
            }}
            title="Delete this profile"
          >
            Delete
          </button>
        </div>
      </header>

      {view === "overview" && (
        <OverviewPanel config={activeProfile} onNavigate={(target) => setView(target)} />
      )}

      {view === "logs" && (
        <>
          <FilterBar
            level={level}
            onLevelChange={setLevel}
            live={live}
            onLiveToggle={setLive}
            sources={sources}
            selectedSources={selectedSources}
            onSelectedSourcesChange={setSelectedSources}
            eventCount={visible.length}
          />

          {error && <div className="error-banner">{error}</div>}

          <main className="rows">
            {visible.length === 0 && !error && (
              <div className="empty">
                No events yet. The ingester writes a row each time a Step Functions
                execution starts, succeeds, or fails.
              </div>
            )}
            {visible.map((e) => (
              <LogRow
                key={e.event_id}
                event={e}
                cadenceBySource={cadenceBySource}
                onClick={() => setDrillDown(e)}
              />
            ))}
          </main>

          {drillDown && <DrillDown event={drillDown} onClose={() => setDrillDown(null)} />}
        </>
      )}

      {view === "integrations" && <IntegrationsPanel config={activeProfile} />}

      {view === "pipeline" && <PipelinePanel config={activeProfile} />}

      {view === "modelgraph" && <InternalLineagePanel config={activeProfile} />}

      {view === "winback" && <WinbackPanel config={activeProfile} />}

      {view === "alarms" && <AlarmsPanel config={activeProfile} />}
    </div>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiHttpError) {
    return `${e.status} ${e.code}: ${e.message}${e.requestId ? ` (req ${e.requestId})` : ""}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
